/**
 * Authentication Hooks
 * 
 * Provides React hooks for authentication operations with Cognito
 */

import { useCallback, useEffect } from 'react';
import { useAuthStore, authSelectors } from '@/stores/authStore';
import { cognitoService } from '@/services/cognito';

/**
 * Main authentication hook
 * 
 * Provides access to authentication state and operations
 */
export const useAuth = () => {
  const store = useAuthStore();
  
  return {
    // State
    user: useAuthStore(authSelectors.user),
    role: useAuthStore(authSelectors.role),
    tenantId: useAuthStore(authSelectors.tenantId),
    accessToken: useAuthStore(authSelectors.accessToken),
    isAuthenticated: useAuthStore(authSelectors.isAuthenticated),
    isLoading: useAuthStore(authSelectors.isLoading),
    
    // Actions
    setAuth: store.setAuth,
    clearAuth: store.clearAuth,
    setUser: store.setUser,
    setLoading: store.setLoading,
    
    // Helpers
    isTokenExpired: store.isTokenExpired,
    shouldRefreshToken: store.shouldRefreshToken,
    hasRole: (role: string) => authSelectors.hasRole(store, role),
    hasAnyRole: (roles: string[]) => authSelectors.hasAnyRole(store, roles),
    hasGroup: (group: string) => authSelectors.hasGroup(store, group),
  };
};

/**
 * Logout hook
 * 
 * Provides logout functionality with Cognito
 */
export const useLogout = () => {
  const { clearAuth } = useAuth();

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Clear auth store first
      clearAuth();
      
      // Redirect to Cognito logout
      cognitoService.redirectToLogout();
    } catch (error) {
      // Always clear auth store even if logout fails
      clearAuth();
      localStorage.removeItem('auth-storage');
      throw error;
    }
  }, [clearAuth]);

  return { logout };
};

/**
 * Token refresh hook
 * 
 * Provides automatic token refresh functionality
 */
export const useRefreshToken = () => {
  const { setAuth, clearAuth, shouldRefreshToken, isAuthenticated } = useAuth();

  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      // Get current refresh token from store
      const currentRefreshToken = useAuthStore.getState().refreshToken;
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      // Refresh token with Cognito
      const tokens = await cognitoService.refreshToken(currentRefreshToken);
      
      // Get user info from the new ID token
      const userInfo = cognitoService.getUserInfo(tokens.id_token);
      if (!userInfo) {
        throw new Error('Failed to decode user info from token');
      }

      // Build user object from token claims
      const currentUser = useAuthStore.getState().user;
      const user = currentUser || {
        id: userInfo.sub,
        id_tenant: userInfo.tenant || 'unknown',
        name_completed: userInfo.name || userInfo.email,
        email: userInfo.email,
        cc_nit: '',
        rol: 'Operativo' as const,
        groups: userInfo.groups || [],
        pendientes: [],
        politics: true,
        tags: [],
        notification_state: false,
        defaults_count: 0,
        defaults_amount: 0,
        createdAt: { date: new Date().toISOString(), author: 'system' },
        updatedAt: { date: new Date().toISOString(), author: 'system' },
      };
      
      // Update auth store with new tokens
      setAuth(user, tokens.id_token, tokens.refresh_token || currentRefreshToken, tokens.expires_in);
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, clear auth and force re-login
      clearAuth();
      throw error;
    }
  }, [setAuth, clearAuth]);

  // Automatic token refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check every minute if token needs refresh
    const interval = setInterval(() => {
      if (shouldRefreshToken()) {
        refreshToken().catch((error) => {
          console.error('Auto token refresh failed:', error);
        });
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, shouldRefreshToken, refreshToken]);

  return { refreshToken };
};

/**
 * Session initialization hook
 * 
 * Checks for existing session on app load from authStore (persisted in localStorage)
 */
export const useInitializeAuth = () => {
  const { setAuth, clearAuth, setLoading, isTokenExpired, shouldRefreshToken } = useAuth();

  const initializeAuth = useCallback(async (): Promise<void> => {
    setLoading(true);
    
    try {
      // Skip authentication in development mode
      if (import.meta.env.VITE_SKIP_AUTH === 'true') {
        // Set mock user for development
        const mockUser = {
          id: 'dev-user-123',
          id_tenant: 'tenant-dev-123',
          name_completed: 'Developer User',
          email: 'dev@example.com',
          cc_nit: '123456789',
          cellphone: '3001234567',
          rol: 'Administrador' as const,
          groups: ['Administrador', 'Coordinador'],
          pendientes: [],
          politics: true,
          tags: [] as string[],
          notification_state: false,
          defaults_count: 0,
          defaults_amount: 0,
          createdAt: { date: new Date().toISOString(), author: 'system' },
          updatedAt: { date: new Date().toISOString(), author: 'system' },
        };
        
        // Set mock token with long expiration (24 hours)
        setAuth(mockUser, 'dev-mock-token', 'dev-refresh-token', 86400);
        return;
      }

      // Get current refresh token from persisted store
      const currentRefreshToken = useAuthStore.getState().refreshToken;

      // Check if token is expired
      if (isTokenExpired()) {
        console.log('Token expired, attempting refresh...');
        
        if (!currentRefreshToken) {
          console.log('No refresh token available, clearing auth');
          clearAuth();
          return;
        }

        // Try to refresh token using cognitoService
        try {
          const tokens = await cognitoService.refreshToken(currentRefreshToken);
          
          // Get user info from token
          const userInfo = cognitoService.getUserInfo(tokens.id_token);
          const currentUser = useAuthStore.getState().user;
          const user = currentUser || {
            id: userInfo?.sub || 'unknown',
            id_tenant: userInfo?.tenant || 'unknown',
            name_completed: userInfo?.name || userInfo?.email || 'Unknown',
            email: userInfo?.email || '',
            cc_nit: '',
            rol: 'Operativo' as const,
            groups: userInfo?.groups || [],
            pendientes: [],
            politics: true,
            tags: [] as string[],
            notification_state: false,
            defaults_count: 0,
            defaults_amount: 0,
            createdAt: { date: new Date().toISOString(), author: 'system' },
            updatedAt: { date: new Date().toISOString(), author: 'system' },
          };
          
          setAuth(user, tokens.id_token, tokens.refresh_token || currentRefreshToken, tokens.expires_in);
          return;
        } catch (refreshError) {
          console.error('Token refresh failed during init:', refreshError);
          clearAuth();
          return;
        }
      }
      
      // Token is valid, check if we need to refresh it proactively
      if (shouldRefreshToken() && currentRefreshToken) {
        console.log('Token expiring soon, refreshing proactively...');
        try {
          const tokens = await cognitoService.refreshToken(currentRefreshToken);
          
          const userInfo = cognitoService.getUserInfo(tokens.id_token);
          const currentUser = useAuthStore.getState().user;
          const user = currentUser || {
            id: userInfo?.sub || 'unknown',
            id_tenant: userInfo?.tenant || 'unknown',
            name_completed: userInfo?.name || userInfo?.email || 'Unknown',
            email: userInfo?.email || '',
            cc_nit: '',
            rol: 'Operativo' as const,
            groups: userInfo?.groups || [],
            pendientes: [],
            politics: true,
            tags: [] as string[],
            notification_state: false,
            defaults_count: 0,
            defaults_amount: 0,
            createdAt: { date: new Date().toISOString(), author: 'system' },
            updatedAt: { date: new Date().toISOString(), author: 'system' },
          };
          
          setAuth(user, tokens.id_token, tokens.refresh_token || currentRefreshToken, tokens.expires_in);
        } catch (refreshError) {
          console.warn('Proactive token refresh failed:', refreshError);
          // Don't clear auth here, token is still valid
        }
      }
      
      // If we reach here, session is valid (either from store or refreshed)
      setLoading(false);
      
    } catch (error) {
      console.error('Session initialization failed:', error);
      clearAuth();
    }
  }, [setAuth, clearAuth, setLoading, isTokenExpired, shouldRefreshToken]);

  // Initialize on mount
  useEffect(() => {
    void initializeAuth();
  }, [initializeAuth]);

  return { initializeAuth };
};
